import { isResolvableCtaPath } from "@care-by-ella/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  COMPLETABLE_MODULE_TYPES,
  FLOW_APPROVER_ROLES,
  FLOW_CHAT_ATTRIBUTIONS,
  FLOW_EVENTS,
  FLOW_MODULE_TYPES,
  RENDERED_SURFACES,
  type ArticleSummary,
  type FlowApproverRole,
  type FlowChatAttribution,
  type FlowContent,
  type FlowEvent,
  type FlowItem,
  type FlowModuleType,
  type FlowSurface,
  type FlowTrigger,
  type LocalizedText,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import { fetchArticles } from "@/lib/articles-api";
import {
  archiveFlow,
  createFlow,
  fetchFlow,
  publishFlow,
  saveFlowDraft,
} from "@/lib/flows-api";
import {
  FlowCalendarGrid,
  paletteColor,
  type CalendarCell,
} from "@/components/FlowCalendarGrid";
import { ViewToggle } from "@/components/ViewToggle";
import { CalendarLegend } from "@/components/CalendarLegend";
import { flowContentTitle } from "@/lib/flow-content";
import { FLOW_EVENT_LABELS, TRIGGER_EVENTS } from "@/lib/flow-event-labels";

const moduleColor = (t: FlowModuleType) =>
  paletteColor(Math.max(0, FLOW_MODULE_TYPES.indexOf(t)));

function stepTitle(item: FlowItem): string {
  return (
    flowContentTitle(item.content, item.moduleType) ||
    MODULE_TYPE_LABELS[item.moduleType] ||
    "(utan titel)"
  );
}

// TES-230 — a flow step's content-module type. `chat_message` authoring lands in
// TES-233, so it's excluded from the picker here.
const MODULE_TYPE_LABELS: Record<FlowModuleType, string> = {
  content: "Innehållskort",
  task: "Uppgift",
  check_in: "Check-in",
  reflection: "Reflektion",
  resource: "Resurs",
  medication: "Medicinering",
  assessment: "Bedömning",
  article: "Artikel",
  chat_message: "Chattmeddelande",
};
const MODULE_TYPE_OPTIONS = FLOW_MODULE_TYPES;
const isCompletable = (t: FlowModuleType) =>
  COMPLETABLE_MODULE_TYPES.includes(t);
const ATTRIBUTION_LABELS: Record<FlowChatAttribution, string> = {
  coach: "Coach",
  doctor: "Läkare",
};

// TES-165 — the day-grid flow builder. Mirrors the intake-template editor's
// draft/publish/version UX (IntakeEditor.tsx): a flow is authored as a draft,
// saved, then published to a new immutable version. Items are placed on
// relative day offsets (day 0 = the assignment anchor) and grouped per day.

// TES-351 — the picker leads with the LIVE surfaces (contracts
// RENDERED_SURFACES): home_now is the canonical home action surface since the
// TES-311 rework retired the home_card feed. home_card stays selectable so
// existing flows round-trip visibly, but is labelled inert.
const SURFACES: { key: FlowSurface; label: string }[] = [
  { key: "home_now", label: "Just nu (§2)" },
  { key: "push", label: "Push" },
  { key: "email", label: "E-post" },
  { key: "home_card", label: "Hemkort (legacy – visas ej)" },
];

const emptyLocalized = (): LocalizedText => ({ sv: "", en: "" });

type EditableFlow = {
  name: string;
  description: string;
  trigger: FlowTrigger;
  items: FlowItem[];
  // TES-284 — package header fields, carried through losslessly so editing a
  // package here doesn't downgrade it to a plain flow. This editor doesn't yet
  // author packages (they're seeded / API-authored), so plain flows keep the
  // 'flow' default; the fields just round-trip.
  pkg: PackageHeaderFields;
  // TES-370 — opaque author metadata (Codex "Ögonblick" fields). Read-only in
  // this editor; carried through losslessly so a save/publish never drops it.
  authorMetadata?: FlowContent["authorMetadata"];
  // QA-baseline 2026-08-19 — TEST_ONLY marker; read-only round-trip here,
  // enforcement is server-side at assignment.
  testOnly: boolean;
};

// TES-284 — the non-item package header, extracted from FlowContent.
type PackageHeaderFields = Pick<
  FlowContent,
  | "kind"
  | "theme"
  | "riskLevel"
  | "packageApprovalRequired"
  | "requiredRole"
  | "patientVisibility"
  | "expiresAfterDays"
  | "themeBehavior"
  | "packageTheme"
  | "completesAssignmentOn"
>;

function defaultPackageHeader(): PackageHeaderFields {
  return {
    kind: "flow",
    packageApprovalRequired: false,
    themeBehavior: "preserve_weekly",
    completesAssignmentOn: [],
  };
}

// TES-372 — Swedish labels for the five theme behaviors (package banner).
const THEME_BEHAVIOR_LABELS: Record<string, string> = {
  preserve_weekly: "påverkar inte veckotemat",
  contextualize_weekly: "kontextualiserar veckotemat",
  set_journey_context: "sätter eget tema",
  start_new_weekly_theme: "startar nytt veckotema",
  override_for_safety: "säkerhetspaus av temat",
  override_for_you_only: "tar över För dig idag",
};

function emptyFlow(): EditableFlow {
  return {
    name: "",
    description: "",
    trigger: { type: "manual" },
    items: [],
    pkg: defaultPackageHeader(),
  testOnly: false,
  };
}

function newItem(dayOffset: number): FlowItem {
  return {
    dayOffset,
    timeOfDay: "09:00",
    order: 0,
    moduleType: "content",
    // TES-351 — new items default to the live home surface. homeVisibility is
    // home_card-only (legacy feed semantics) so it's no longer defaulted; the
    // toggle handler adds it back when home_card is selected.
    surfaces: ["home_now"],
    // TES-285 per-module defaults (authoring inputs for these land with the
    // package/priority editors; new items start with the sensible defaults).
    priority: 50,
    required: false,
    blocksNextState: false,
    fallback: false,
    approvalRequired: false,
    content: { home: { title: emptyLocalized(), body: emptyLocalized() } },
  };
}

// Key-canonical JSON so the dirty comparison is insensitive to object key
// order — an edited item is rebuilt via spreads and can differ in key insertion
// order from the server-loaded DTO even when values are identical.
function canonicalJson(value: unknown): string {
  return JSON.stringify(value, (_key, val) =>
    val && typeof val === "object" && !Array.isArray(val)
      ? Object.keys(val as Record<string, unknown>)
          .sort()
          .reduce<Record<string, unknown>>((acc, k) => {
            acc[k] = (val as Record<string, unknown>)[k];
            return acc;
          }, {})
      : val,
  );
}

// The normalized request body sent on save/publish. Pure so it can also produce
// a baseline snapshot for dirty-tracking (edited-vs-published comparison).
function buildFlowPayload(d: EditableFlow) {
  return {
    name: d.name,
    description: d.description || undefined,
    trigger: d.trigger,
    // TES-284 — round-trip the package header (defaults 'flow' for plain flows).
    kind: d.pkg.kind,
    theme: d.pkg.theme,
    riskLevel: d.pkg.riskLevel,
    packageApprovalRequired: d.pkg.packageApprovalRequired,
    requiredRole: d.pkg.requiredRole,
    patientVisibility: d.pkg.patientVisibility,
    expiresAfterDays: d.pkg.expiresAfterDays,
    // TES-372 — round-trip the theme interaction (read-only banner here).
    themeBehavior: d.pkg.themeBehavior,
    packageTheme: d.pkg.packageTheme,
    // Package-level completion; read-only round-trip here.
    completesAssignmentOn: d.pkg.completesAssignmentOn,
    // TES-370 — round-trip the opaque author metadata (read-only here).
    authorMetadata: d.authorMetadata,
    testOnly: d.testOnly,
    items: d.items.map((it, i) => {
      const item = { ...it, order: i };
      // completesOn is kept across type switches for lossless round-trips, so
      // strip it here when the final type isn't completable (the schema only
      // allows it on task/check_in/reflection/assessment/medication).
      if (!isCompletable(item.moduleType)) item.completesOn = undefined;
      // Drop any type-specific content carried over from a prior type so the
      // persisted step has no orphan payload: chatMessage only on chat_message,
      // article only on article.
      const content = { ...item.content };
      if (item.moduleType !== "chat_message") delete content.chatMessage;
      if (item.moduleType !== "article") delete content.article;
      // chat_message is delivered to chat, not a card/push/email surface. The
      // surface state kept in the editing model (for lossless round-trips) is
      // normalised away here — surfaces empty, only the chat content persists.
      if (item.moduleType === "chat_message") {
        return {
          ...item,
          surfaces: [],
          homeVisibility: undefined,
          content: { chatMessage: content.chatMessage },
        };
      }
      return { ...item, content };
    }),
  };
}

// Map a server FlowContent (published or draft) into the editable model — used
// by the loader and by the TES-363 diff (both sides normalize through
// buildFlowPayload so defaults/ordering can't produce phantom differences).
function toEditable(base: FlowContent): EditableFlow {
  return {
    name: base.name,
    description: base.description ?? "",
    trigger: base.trigger,
    items: base.items,
    pkg: {
      kind: base.kind,
      theme: base.theme,
      riskLevel: base.riskLevel,
      packageApprovalRequired: base.packageApprovalRequired,
      requiredRole: base.requiredRole,
      patientVisibility: base.patientVisibility,
      expiresAfterDays: base.expiresAfterDays,
      themeBehavior: base.themeBehavior,
      packageTheme: base.packageTheme,
      completesAssignmentOn: base.completesAssignmentOn,
    },
    authorMetadata: base.authorMetadata,
    testOnly: base.testOnly ?? false,
  };
}

// --- TES-363: draft-vs-published field diff ---------------------------------
// A deliberately SIMPLE summary (per-item added/removed/changed + which fields
// changed), not a text diff: enough for a reviewer to see what publishing
// would change. Items match by their persisted id (flowItemId), falling back
// to (dayOffset, order) for id-less rows (e.g. a re-imported draft).

type NormalizedItem = FlowItem & { order: number };

type FlowItemDiffRow = {
  key: string;
  title: string;
  day: number;
  kind: "added" | "removed" | "changed";
  fields?: string[];
};
type FlowDiff = { header: string[]; items: FlowItemDiffRow[] };

const diffEq = (a: unknown, b: unknown) =>
  canonicalJson(a ?? null) === canonicalJson(b ?? null);

const ITEM_DIFF_FIELDS: [string, (i: NormalizedItem) => unknown][] = [
  ["dag", (i) => i.dayOffset],
  ["tid", (i) => i.timeOfDay],
  ["typ", (i) => i.moduleType],
  ["ytor", (i) => [...i.surfaces].sort()],
  ["prioritet", (i) => i.priority],
  ["krävs", (i) => i.required],
  ["blockerande", (i) => i.blocksNextState],
  ["reserv", (i) => i.fallback],
  ["visningsdagar", (i) => i.durationDays ?? null],
  [
    "godkännande",
    (i) => (i.approvalRequired ? (i.approverRole ?? "ja") : false),
  ],
  ["slutförs av händelse", (i) => i.completesOn ?? null],
  ["synlighet", (i) => i.homeVisibility ?? null],
  ["heminnehåll", (i) => i.content.home ?? null],
  ["push-innehåll", (i) => i.content.push ?? null],
  ["e-postinnehåll", (i) => i.content.email ?? null],
  ["artikel", (i) => i.content.article ?? null],
  ["chattmeddelande", (i) => i.content.chatMessage ?? null],
];

const HEADER_DIFF_FIELDS: [
  string,
  (p: ReturnType<typeof buildFlowPayload>) => unknown,
][] = [
  ["namn", (p) => p.name],
  ["beskrivning", (p) => p.description ?? null],
  ["trigger", (p) => p.trigger],
  ["typ (flöde/paket)", (p) => p.kind],
  ["tema", (p) => p.theme ?? null],
  ["risknivå", (p) => p.riskLevel ?? null],
  ["paketgodkännande", (p) => p.packageApprovalRequired ?? false],
  ["godkännanderoll", (p) => p.requiredRole ?? null],
  ["patientsynlighet", (p) => p.patientVisibility ?? null],
  ["utgår efter dagar", (p) => p.expiresAfterDays ?? null],
  ["temabeteende", (p) => p.themeBehavior ?? "preserve_weekly"],
  ["pakettema", (p) => p.packageTheme ?? null],
];

function diffFlows(published: FlowContent, current: EditableFlow): FlowDiff {
  const pub = buildFlowPayload(toEditable(published));
  const cur = buildFlowPayload(current);

  const header = HEADER_DIFF_FIELDS.filter(
    ([, get]) => !diffEq(get(pub), get(cur)),
  ).map(([label]) => label);

  const pubItems = pub.items as NormalizedItem[];
  const curItems = cur.items as NormalizedItem[];
  const rowTitle = (i: NormalizedItem) => stepTitle(i);

  const rows: FlowItemDiffRow[] = [];
  const matchedPub = new Set<NormalizedItem>();
  const pushChanged = (p: NormalizedItem, c: NormalizedItem, key: string) => {
    const fields = ITEM_DIFF_FIELDS.filter(
      ([, get]) => !diffEq(get(p), get(c)),
    ).map(([label]) => label);
    if (fields.length > 0) {
      rows.push({
        key,
        title: rowTitle(c),
        day: c.dayOffset,
        kind: "changed",
        fields,
      });
    }
  };

  // Pass 1: persisted id (the template's flowItemId).
  const pubById = new Map(
    pubItems.filter((i) => i.id).map((i) => [i.id!, i] as const),
  );
  const unmatchedCur: NormalizedItem[] = [];
  for (const c of curItems) {
    const p = c.id ? pubById.get(c.id) : undefined;
    if (p && !matchedPub.has(p)) {
      matchedPub.add(p);
      pushChanged(p, c, `id:${c.id}`);
    } else {
      unmatchedCur.push(c);
    }
  }
  // Pass 2: (dayOffset, order) for id-less rows.
  const remaining = new Map<string, NormalizedItem>(
    pubItems
      .filter((p) => !matchedPub.has(p))
      .map((p) => [`${p.dayOffset}:${p.order}`, p]),
  );
  for (const c of unmatchedCur) {
    const key = `${c.dayOffset}:${c.order}`;
    const p = remaining.get(key);
    if (p) {
      remaining.delete(key);
      matchedPub.add(p);
      pushChanged(p, c, `pos:${key}`);
    } else {
      rows.push({
        key: `added:${key}`,
        title: rowTitle(c),
        day: c.dayOffset,
        kind: "added",
      });
    }
  }
  for (const p of pubItems) {
    if (!matchedPub.has(p)) {
      rows.push({
        key: `removed:${p.id ?? `${p.dayOffset}:${p.order}`}`,
        title: rowTitle(p),
        day: p.dayOffset,
        kind: "removed",
      });
    }
  }
  rows.sort((a, b) => a.day - b.day);
  return { header, items: rows };
}

const DIFF_KIND_META: Record<
  FlowItemDiffRow["kind"],
  { label: string; cls: string }
> = {
  added: { label: "Ny", cls: "bg-success/10 text-success border-success/30" },
  removed: {
    label: "Tas bort",
    cls: "bg-danger/10 text-danger border-danger/30",
  },
  changed: {
    label: "Ändrad",
    cls: "bg-warning/10 text-warning border-warning/30",
  },
};

function FlowDiffPanel({
  diff,
  publishedVersion,
}: {
  diff: FlowDiff;
  publishedVersion: number;
}) {
  const empty = diff.header.length === 0 && diff.items.length === 0;
  return (
    <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm">
      <h2 className="font-display text-lg">
        Ändringar mot publicerad v{publishedVersion}
      </h2>
      {empty ? (
        <p className="text-sm text-textSecondary">
          Inga skillnader mot den publicerade versionen.
        </p>
      ) : (
        <>
          {diff.header.length > 0 ? (
            <p className="text-sm">
              <span className="text-textSecondary">
                Flödesinställningar ändrade:{" "}
              </span>
              {diff.header.join(", ")}
            </p>
          ) : null}
          {diff.items.length > 0 ? (
            <ul className="flex flex-col gap-xs">
              {diff.items.map((row) => (
                <li
                  key={row.key}
                  className="flex items-start gap-sm text-sm border-b border-surfaceMuted last:border-b-0 py-xs"
                >
                  <span
                    className={`inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border shrink-0 ${DIFF_KIND_META[row.kind].cls}`}
                  >
                    {DIFF_KIND_META[row.kind].label}
                  </span>
                  <span className="pt-xs">
                    <span className="font-medium">{row.title}</span>
                    <span className="text-textSecondary"> · Dag {row.day}</span>
                    {row.kind === "changed" && row.fields ? (
                      <span className="text-textSecondary">
                        {" "}
                        — {row.fields.join(", ")}
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </section>
  );
}

// --- shared inputs ---------------------------------------------------------

function LocalizedInput({
  label,
  value,
  onChange,
  multiline = false,
}: {
  label: string;
  value: LocalizedText;
  onChange: (next: LocalizedText) => void;
  multiline?: boolean;
}) {
  const base =
    "w-full border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand";
  return (
    <div className="flex flex-col gap-xs">
      <span className="text-xs uppercase tracking-wide text-textSecondary">
        {label}
      </span>
      <div className="grid grid-cols-2 gap-sm">
        {(["sv", "en"] as const).map((locale) => {
          const common = {
            value: value[locale],
            placeholder: locale.toUpperCase(),
            onChange: (
              e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => onChange({ ...value, [locale]: e.target.value }),
          };
          return (
            <div key={locale} className="flex flex-col gap-xs">
              <span className="text-[10px] font-medium text-textSecondary">
                {locale === "sv" ? "Svenska" : "Engelska"}
              </span>
              {multiline ? (
                <textarea {...common} className={`${base} min-h-[60px]`} />
              ) : (
                <input {...common} className={base} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlainField({
  label,
  value,
  onChange,
  placeholder,
  mono = false,
  warning,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
  // TES-362: inline validation hint (e.g. an unresolvable ctaPath).
  warning?: string | null;
}) {
  return (
    <label className="flex flex-col gap-xs">
      <span className="text-xs uppercase tracking-wide text-textSecondary">
        {label}
      </span>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand ${
          mono ? "font-mono" : ""
        }`}
      />
      {warning ? <span className="text-xs text-danger">{warning}</span> : null}
    </label>
  );
}

// TES-362 — author-time hint for CTA paths: the save validates via contracts
// resolvableCtaPathSchema anyway, but surfacing it while typing beats a 422.
function ctaPathWarning(value: string): string | null {
  if (!value || isResolvableCtaPath(value)) return null;
  return "Okänd länk — använd carebyella://weekly-checkin|health-declaration|blood-test|treatment|chat, carebyella://article/<nyckel>, carebyella://milestone/<n> eller en https-URL.";
}

// --- item editor -----------------------------------------------------------

function ItemEditor({
  item,
  onChange,
  onRemove,
  articles,
}: {
  item: FlowItem;
  onChange: (next: FlowItem) => void;
  onRemove: () => void;
  articles: ArticleSummary[];
}) {
  const has = (s: FlowSurface) => item.surfaces.includes(s);
  const moduleType = item.moduleType ?? "content";
  const isChat = moduleType === "chat_message";
  // TES-363 — surfaces the picker doesn't cover (imported items can carry any
  // FLOW_SURFACES value, e.g. home_for_you). Shown as chips below so the
  // reviewer SEES them; anything outside contracts RENDERED_SURFACES renders
  // nowhere on mobile today and is flagged inert.
  const extraSurfaces = item.surfaces.filter(
    (s) => !SURFACES.some((p) => p.key === s),
  );
  const isInert = (s: FlowSurface) =>
    !(RENDERED_SURFACES as readonly string[]).includes(s);

  function toggleSurface(s: FlowSurface, on: boolean) {
    const surfaces = on
      ? [...new Set([...item.surfaces, s])]
      : item.surfaces.filter((x) => x !== s);
    const content = { ...item.content };
    // TES-351: home_now shares the `home` content shape with legacy home_card.
    const isHomeSurface = (x: FlowSurface) =>
      x === "home_card" || x === "home_now";
    if (on) {
      if (isHomeSurface(s) && !content.home)
        content.home = { title: emptyLocalized(), body: emptyLocalized() };
      if (s === "push" && !content.push)
        content.push = { title: emptyLocalized(), body: emptyLocalized() };
      if (s === "email" && !content.email)
        content.email = {
          subject: emptyLocalized(),
          heading: emptyLocalized(),
          body: emptyLocalized(),
        };
    } else {
      // Only drop home content when NO home surface remains.
      if (isHomeSurface(s) && !surfaces.some(isHomeSurface))
        delete content.home;
      if (s === "push") delete content.push;
      if (s === "email") delete content.email;
    }
    onChange({
      ...item,
      surfaces,
      content,
      homeVisibility: surfaces.includes("home_card")
        ? (item.homeVisibility ?? "until_completed")
        : undefined,
    });
  }

  const home = item.content.home;
  const push = item.content.push;
  const email = item.content.email;

  return (
    <div className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
      <div className="flex flex-wrap items-end gap-md">
        <label className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Dag
          </span>
          <input
            type="number"
            min={0}
            value={item.dayOffset}
            onChange={(e) =>
              onChange({
                ...item,
                dayOffset: Math.max(0, Number(e.target.value) || 0),
              })
            }
            className="w-20 border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
          />
        </label>
        <label className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Tid
          </span>
          <input
            type="time"
            value={item.timeOfDay}
            onChange={(e) => onChange({ ...item, timeOfDay: e.target.value })}
            className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
          />
        </label>
        <label className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Typ
          </span>
          <select
            value={moduleType}
            onChange={(e) => {
              const next = e.target.value as FlowModuleType;
              // Non-destructive: never delete authored surface content or flip
              // homeVisibility on a type switch. chat_message hides its surface
              // UI (below) and is normalised to no-surface only at save time, so
              // switching to it and back is lossless. We only (a) seed the new
              // type's required payload, and (b) rebuild a surface when switching
              // a surface-less item (e.g. a reloaded chat_message) to a card type
              // that requires ≥1 — preferring whatever content already exists.
              const content = { ...item.content };
              let surfaces = item.surfaces;
              let homeVisibility = item.homeVisibility;
              if (next === "chat_message") {
                if (!content.chatMessage)
                  content.chatMessage = {
                    attribution: "coach",
                    body: emptyLocalized(),
                    generationStrategy: "template",
                  };
              } else {
                if (next === "article" && !content.article)
                  content.article = { articleKey: "" };
                // TES-351: a completable step (task/check-in/…) is acted on in
                // "Vad händer just nu", so it must carry the home_now surface —
                // the contract enforces this (home_card renders nowhere now).
                if (isCompletable(next) && !surfaces.includes("home_now")) {
                  surfaces = [...surfaces, "home_now"];
                }
                // Every non-chat step needs ≥1 surface; rebuild from existing
                // content when none remain (e.g. a reloaded chat_message).
                if (surfaces.length === 0) {
                  surfaces = [
                    ...(content.home ? (["home_now"] as const) : []),
                    ...(content.push ? (["push"] as const) : []),
                    ...(content.email ? (["email"] as const) : []),
                  ];
                  if (surfaces.length === 0) surfaces = ["home_now"];
                }
                // Whenever a home surface is present, ensure its content;
                // homeVisibility stays home_card-only (legacy feed semantics).
                if (
                  surfaces.includes("home_card") ||
                  surfaces.includes("home_now")
                ) {
                  if (!content.home)
                    content.home = {
                      title: emptyLocalized(),
                      body: emptyLocalized(),
                    };
                }
                if (surfaces.includes("home_card")) {
                  homeVisibility = homeVisibility ?? "until_completed";
                }
              }
              onChange({
                ...item,
                moduleType: next,
                surfaces,
                homeVisibility,
                // completesOn is kept across the switch (lossless round-trip)
                // and normalised away at save for non-completable types.
                content,
              });
            }}
            className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
          >
            {MODULE_TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {MODULE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
        {!isChat ? (
          <div className="flex flex-col gap-xs">
            <span className="text-xs uppercase tracking-wide text-textSecondary">
              Ytor
            </span>
            <div className="flex gap-md pt-xs">
              {SURFACES.map((s) => {
                // A non-chat step needs ≥1 surface; block unchecking the last
                // one so it can't reach an empty, unpublishable state.
                const isLast = has(s.key) && item.surfaces.length === 1;
                // Completable steps are completed on their Home card, so Hemkort
                // is required for them and can't be unchecked.
                const isLockedHome =
                  s.key === "home_card" &&
                  isCompletable(moduleType) &&
                  has(s.key);
                const lockReason = isLockedHome
                  ? "Uppgifter visas alltid på hemskärmen"
                  : isLast
                    ? "Minst en yta krävs"
                    : undefined;
                return (
                  <label
                    key={s.key}
                    className="flex items-center gap-xs text-sm text-textPrimary"
                    title={lockReason}
                  >
                    <input
                      type="checkbox"
                      checked={has(s.key)}
                      disabled={isLast || isLockedHome}
                      onChange={(e) => toggleSurface(s.key, e.target.checked)}
                    />
                    {s.label}
                  </label>
                );
              })}
            </div>
            {/* TES-363 — the FULL surfaces list: values outside the picker
                (imports can set any FLOW_SURFACES value) used to be invisible
                here. Inert = validates/materializes but renders nowhere. */}
            {extraSurfaces.length > 0 ? (
              <div className="flex flex-wrap gap-xs pt-xs">
                {extraSurfaces.map((s) => {
                  const isLastSurface = item.surfaces.length === 1;
                  return (
                    <span
                      key={s}
                      className={`inline-flex items-center gap-xs px-sm py-xs rounded-pill text-xs font-medium border ${
                        isInert(s)
                          ? "bg-surfaceMuted text-textSecondary border-surfaceMuted"
                          : "bg-brand/10 text-brand border-brand/30"
                      }`}
                      title={
                        isInert(s)
                          ? "Ytan är inaktiv — innehåll här når inte patienten idag"
                          : undefined
                      }
                    >
                      <code>{s}</code>
                      {isInert(s) ? "· inaktiv (visas ej)" : null}
                      <button
                        type="button"
                        aria-label={`Ta bort ytan ${s}`}
                        disabled={isLastSurface}
                        title={isLastSurface ? "Minst en yta krävs" : undefined}
                        onClick={() => toggleSurface(s, false)}
                        className="hover:text-danger disabled:opacity-40"
                      >
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}
        <button
          type="button"
          onClick={onRemove}
          className="ml-auto px-sm py-xs rounded-md border border-surfaceMuted text-sm text-textSecondary hover:text-danger hover:border-danger"
        >
          Ta bort
        </button>
      </div>

      {/* A completable step that isn't on Home can never be completed by the
          patient — nudge the author to add the Hemkort surface. */}
      {isCompletable(moduleType) && !has("home_card") ? (
        <div className="bg-warning/10 border border-warning/30 text-warning rounded-md px-sm py-xs text-xs">
          Den här uppgiften slutförs på hemskärmen — kryssa i{" "}
          <strong>Hemkort</strong> så att patienten ser den.
        </div>
      ) : null}

      {/* TES-363 — the TES-285 ranking/gating fields, previously round-tripped
          invisibly. Editable: they drive the §2 ladder (priority/required/
          blocksNextState/fallback), the visibility window (durationDays) and
          the per-module approval gate. Hidden for chat_message — its delivery
          rides the TES-233 draft-approval pipeline, not §2 candidacy. */}
      {!isChat ? (
        <div className="flex flex-col gap-sm border-t border-surfaceMuted pt-md">
          <h4 className="text-sm font-medium">
            Prioritering &amp; regler
            <span className="ml-xs text-xs font-normal text-textSecondary">
              · styr “Vad händer just nu” (§2)
            </span>
          </h4>
          <div className="flex flex-wrap items-end gap-md">
            <label className="flex flex-col gap-xs">
              <span className="text-xs uppercase tracking-wide text-textSecondary">
                Prioritet (1–100)
              </span>
              <input
                type="number"
                min={1}
                max={100}
                value={item.priority}
                onChange={(e) =>
                  onChange({
                    ...item,
                    priority: Math.min(
                      100,
                      Math.max(1, Number(e.target.value) || 1),
                    ),
                  })
                }
                className="w-24 border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
              />
            </label>
            <label className="flex flex-col gap-xs">
              <span className="text-xs uppercase tracking-wide text-textSecondary">
                Visas i antal dagar
              </span>
              <input
                type="number"
                min={1}
                value={item.durationDays ?? ""}
                placeholder="∞"
                title="Tomt = ingen bortre gräns"
                onChange={(e) => {
                  const n = Number(e.target.value);
                  onChange({
                    ...item,
                    durationDays:
                      e.target.value === "" || !Number.isFinite(n) || n < 1
                        ? null
                        : Math.trunc(n),
                  });
                }}
                className="w-24 border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
              />
            </label>
            <div className="flex flex-wrap gap-md pt-xs">
              <label className="flex items-center gap-xs text-sm text-textPrimary">
                <input
                  type="checkbox"
                  checked={item.required}
                  onChange={(e) =>
                    // A required item can't also be the calm fallback (schema).
                    onChange({
                      ...item,
                      required: e.target.checked,
                      fallback: e.target.checked ? false : item.fallback,
                    })
                  }
                />
                Krävs
              </label>
              <label
                className="flex items-center gap-xs text-sm text-textPrimary"
                title="Visas som blockerande uppgift — mjuk gate, stoppar inte resan tekniskt"
              >
                <input
                  type="checkbox"
                  checked={item.blocksNextState}
                  onChange={(e) =>
                    onChange({
                      ...item,
                      blocksNextState: e.target.checked,
                      fallback: e.target.checked ? false : item.fallback,
                    })
                  }
                />
                Blockerande
              </label>
              <label
                className="flex items-center gap-xs text-sm text-textPrimary"
                title="Lugnt reservinnehåll — visas bara när inget annat kräver uppmärksamhet"
              >
                <input
                  type="checkbox"
                  checked={item.fallback}
                  onChange={(e) =>
                    // A fallback item can't be required/blocking (schema).
                    onChange({
                      ...item,
                      fallback: e.target.checked,
                      required: e.target.checked ? false : item.required,
                      blocksNextState: e.target.checked
                        ? false
                        : item.blocksNextState,
                    })
                  }
                />
                Reserv
              </label>
              <label className="flex items-center gap-xs text-sm text-textPrimary">
                <input
                  type="checkbox"
                  checked={item.approvalRequired}
                  onChange={(e) =>
                    onChange({
                      ...item,
                      approvalRequired: e.target.checked,
                      // The schema requires an approver role when gated; clear
                      // it when the gate is removed to keep the payload clean.
                      approverRole: e.target.checked
                        ? (item.approverRole ?? "doctor")
                        : undefined,
                    })
                  }
                />
                Kräver godkännande
              </label>
              {item.approvalRequired ? (
                <label className="flex items-center gap-xs text-xs text-textSecondary">
                  Godkänns av
                  <select
                    value={item.approverRole ?? "doctor"}
                    onChange={(e) =>
                      onChange({
                        ...item,
                        approverRole: e.target.value as FlowApproverRole,
                      })
                    }
                    className="border border-surfaceMuted rounded-md p-xs text-sm"
                  >
                    {FLOW_APPROVER_ROLES.map((r) => (
                      <option key={r} value={r}>
                        {r === "doctor" ? "Läkare" : "Coach"}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {/* TES-230 — per-type fields */}
      {moduleType === "article" ? (
        <label className="flex flex-col gap-xs border-t border-surfaceMuted pt-md">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Artikel
          </span>
          <select
            value={item.content.article?.articleKey ?? ""}
            onChange={(e) =>
              onChange({
                ...item,
                content: {
                  ...item.content,
                  article: { articleKey: e.target.value },
                },
              })
            }
            className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
          >
            <option value="">Välj artikel…</option>
            {articles.map((a) => (
              <option key={a.key} value={a.key}>
                {a.title.sv || a.key}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {moduleType === "chat_message" && item.content.chatMessage ? (
        <div className="flex flex-col gap-sm border-t border-surfaceMuted pt-md">
          <label className="flex flex-col gap-xs">
            <span className="text-xs uppercase tracking-wide text-textSecondary">
              Avsändare
            </span>
            <select
              value={item.content.chatMessage.attribution}
              onChange={(e) =>
                onChange({
                  ...item,
                  content: {
                    ...item.content,
                    chatMessage: {
                      ...item.content.chatMessage!,
                      attribution: e.target.value as FlowChatAttribution,
                    },
                  },
                })
              }
              className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
            >
              {FLOW_CHAT_ATTRIBUTIONS.map((a) => (
                <option key={a} value={a}>
                  {ATTRIBUTION_LABELS[a]}
                </option>
              ))}
            </select>
          </label>
          <LocalizedInput
            label="Meddelandemall (utkast — godkänns före utskick)"
            value={item.content.chatMessage.body}
            onChange={(body) =>
              onChange({
                ...item,
                content: {
                  ...item.content,
                  chatMessage: { ...item.content.chatMessage!, body },
                },
              })
            }
            multiline
          />
        </div>
      ) : null}

      {isCompletable(moduleType) ? (
        <label className="flex flex-col gap-xs border-t border-surfaceMuted pt-md">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Slutförs automatiskt av händelse (valfritt)
          </span>
          <select
            value={item.completesOn ?? ""}
            onChange={(e) =>
              onChange({
                ...item,
                completesOn: e.target.value
                  ? (e.target.value as FlowEvent)
                  : undefined,
              })
            }
            className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
          >
            <option value="">— Endast manuell —</option>
            {FLOW_EVENTS.map((ev) => (
              <option key={ev} value={ev}>
                {FLOW_EVENT_LABELS[ev]}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {!isChat && (has("home_card") || has("home_now")) && home ? (
        <div className="flex flex-col gap-sm border-t border-surfaceMuted pt-md">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">
              {moduleType === "content"
                ? "Heminnehåll"
                : MODULE_TYPE_LABELS[moduleType]}
              {moduleType !== "content" ? (
                <span className="ml-xs text-xs font-normal text-textSecondary">
                  · på hemskärmen
                </span>
              ) : null}
            </h4>
            {/* Synlighet is legacy home_card feed semantics — home_now items
                are governed by durationDays + task state instead (TES-351). */}
            {has("home_card") ? (
              <label className="flex items-center gap-xs text-xs text-textSecondary">
                Synlighet
                <select
                  value={item.homeVisibility ?? "until_completed"}
                  onChange={(e) =>
                    onChange({
                      ...item,
                      homeVisibility: e.target
                        .value as FlowItem["homeVisibility"],
                    })
                  }
                  className="border border-surfaceMuted rounded-md p-xs text-sm"
                >
                  <option value="until_completed">Tills klar</option>
                  <option value="date_driven">Datumstyrd</option>
                </select>
              </label>
            ) : null}
          </div>
          <LocalizedInput
            label="Titel"
            value={home.title}
            onChange={(title) =>
              onChange({
                ...item,
                content: { ...item.content, home: { ...home, title } },
              })
            }
          />
          <LocalizedInput
            label="Text"
            value={home.body}
            multiline
            onChange={(body) =>
              onChange({
                ...item,
                content: { ...item.content, home: { ...home, body } },
              })
            }
          />
          <LocalizedInput
            label="Knapptext (valfri)"
            value={home.ctaLabel ?? emptyLocalized()}
            onChange={(ctaLabel) =>
              onChange({
                ...item,
                content: { ...item.content, home: { ...home, ctaLabel } },
              })
            }
          />
          <PlainField
            label="Knapp-länk (valfri)"
            value={home.ctaPath ?? ""}
            placeholder="carebyella://blood-test"
            mono
            warning={ctaPathWarning(home.ctaPath ?? "")}
            onChange={(ctaPath) =>
              onChange({
                ...item,
                content: {
                  ...item.content,
                  home: { ...home, ctaPath: ctaPath || undefined },
                },
              })
            }
          />
        </div>
      ) : null}

      {!isChat && has("push") && push ? (
        <div className="flex flex-col gap-sm border-t border-surfaceMuted pt-md">
          <h4 className="text-sm font-medium">Push</h4>
          <LocalizedInput
            label="Titel"
            value={push.title}
            onChange={(title) =>
              onChange({
                ...item,
                content: { ...item.content, push: { ...push, title } },
              })
            }
          />
          <LocalizedInput
            label="Text"
            value={push.body}
            multiline
            onChange={(body) =>
              onChange({
                ...item,
                content: { ...item.content, push: { ...push, body } },
              })
            }
          />
          <PlainField
            label="Knapp-länk (valfri)"
            value={push.ctaPath ?? ""}
            warning={ctaPathWarning(push.ctaPath ?? "")}
            placeholder="carebyella://blood-test"
            mono
            onChange={(ctaPath) =>
              onChange({
                ...item,
                content: {
                  ...item.content,
                  push: { ...push, ctaPath: ctaPath || undefined },
                },
              })
            }
          />
        </div>
      ) : null}

      {!isChat && has("email") && email ? (
        <div className="flex flex-col gap-sm border-t border-surfaceMuted pt-md">
          <h4 className="text-sm font-medium">E-post</h4>
          <LocalizedInput
            label="Ämne"
            value={email.subject}
            onChange={(subject) =>
              onChange({
                ...item,
                content: { ...item.content, email: { ...email, subject } },
              })
            }
          />
          <LocalizedInput
            label="Rubrik"
            value={email.heading}
            onChange={(heading) =>
              onChange({
                ...item,
                content: { ...item.content, email: { ...email, heading } },
              })
            }
          />
          <LocalizedInput
            label="Text"
            value={email.body}
            multiline
            onChange={(body) =>
              onChange({
                ...item,
                content: { ...item.content, email: { ...email, body } },
              })
            }
          />
          <LocalizedInput
            label="Knapptext (valfri)"
            value={email.ctaLabel ?? emptyLocalized()}
            onChange={(ctaLabel) =>
              onChange({
                ...item,
                content: { ...item.content, email: { ...email, ctaLabel } },
              })
            }
          />
          <PlainField
            label="Knapp-länk (valfri)"
            value={email.ctaPath ?? ""}
            warning={ctaPathWarning(email.ctaPath ?? "")}
            placeholder="carebyella://blood-test"
            mono
            onChange={(ctaPath) =>
              onChange({
                ...item,
                content: {
                  ...item.content,
                  email: { ...email, ctaPath: ctaPath || undefined },
                },
              })
            }
          />
        </div>
      ) : null}
    </div>
  );
}

// --- route -----------------------------------------------------------------

export function FlowEditorRoute() {
  const { key: routeKey } = useParams<{ key: string }>();
  const isNew = routeKey === undefined || routeKey === "new";
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const [draft, setDraft] = useState<EditableFlow | null>(
    isNew ? emptyFlow() : null,
  );
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);
  // TES-363 — the full published version (not just its number), kept so the
  // "Visa ändringar" toggle can diff the editor state against what's live.
  const [publishedContent, setPublishedContent] = useState<FlowContent | null>(
    null,
  );
  const [showDiff, setShowDiff] = useState(false);
  const [hadDraft, setHadDraft] = useState(false);
  // Serialized normalized payload as last loaded/saved. `dirty` compares the
  // current draft against it, so edit-then-revert reads as clean and Publicera
  // won't mint a duplicate version. Set on load and after every successful save.
  const [baseline, setBaseline] = useState<string>(() =>
    // A brand-new flow's baseline is the empty flow, so a pristine /flows/new
    // reads clean (no spurious unsaved-changes prompt) until the coach edits it.
    isNew ? canonicalJson(buildFlowPayload(emptyFlow())) : "",
  );
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<"save" | "publish" | "archive" | null>(null);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [view, setView] = useState<"list" | "calendar">("calendar");
  // The step whose form is open in the calendar's edit modal (index into
  // draft.items), or null when the modal is closed.
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  // TES-230 — published articles for the article-step picker.
  const [articles, setArticles] = useState<ArticleSummary[]>([]);

  useEffect(() => {
    fetchArticles({ status: "published" })
      .then(setArticles)
      .catch(() => setArticles([]));
  }, []);

  const load = useCallback(() => {
    if (isNew || !routeKey) return;
    fetchFlow(routeKey)
      .then(({ published, draft: drf }) => {
        const base = drf ?? published;
        setPublishedVersion(published?.version ?? null);
        setPublishedContent(published);
        setHadDraft(drf !== null);
        const loaded: EditableFlow = base ? toEditable(base) : emptyFlow();
        setBaseline(canonicalJson(buildFlowPayload(loaded)));
        setDraft(loaded);
      })
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      });
  }, [isNew, routeKey, signOut]);

  useEffect(load, [load]);

  // Derived: the current draft differs from what was last loaded/saved.
  const dirty = useMemo(
    () => (draft ? canonicalJson(buildFlowPayload(draft)) !== baseline : false),
    [draft, baseline],
  );

  // TES-363 — the field-level draft-vs-published summary behind
  // "Visa ändringar". Diffs the CURRENT editor state (saved draft + unsaved
  // edits) against the live published version.
  const diff = useMemo(
    () =>
      showDiff && publishedContent && draft
        ? diffFlows(publishedContent, draft)
        : null,
    [showDiff, publishedContent, draft],
  );

  // Warn before leaving with unsaved edits (parity with IntakeEditor).
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const groups = useMemo(() => {
    if (!draft) return [];
    const byDay = new Map<number, { item: FlowItem; index: number }[]>();
    draft.items.forEach((item, index) => {
      const bucket = byDay.get(item.dayOffset);
      if (bucket) bucket.push({ item, index });
      else byDay.set(item.dayOffset, [{ item, index }]);
    });
    return [...byDay.entries()].sort((a, b) => a[0] - b[0]);
  }, [draft]);

  // Relative-day calendar: steps placed on their dayOffset, laid out as weeks of
  // 7 days (Dag 0 = the assignment anchor). Empty cells are click-to-add.
  const { weeks: calendarWeeks, truncated: calendarTruncated } = useMemo(() => {
    // Skip the layout when the calendar isn't the live editing surface — in the
    // list view, and while the edit modal covers the grid — so per-keystroke
    // setDraft doesn't rebuild (and reconcile) the whole week matrix underneath.
    if (!draft || view !== "calendar" || editingIndex !== null)
      return { weeks: [] as CalendarCell[][], truncated: false };
    const byDay = new Map<number, CalendarCell["chips"]>();
    draft.items.forEach((item, index) => {
      const chip = {
        id: String(index),
        label: stepTitle(item),
        time: item.timeOfDay,
        color: moduleColor(item.moduleType),
        meta: MODULE_TYPE_LABELS[item.moduleType],
      };
      const bucket = byDay.get(item.dayOffset);
      if (bucket) bucket.push(chip);
      else byDay.set(item.dayOffset, [chip]);
    });
    const maxDay = draft.items.reduce((m, it) => Math.max(m, it.dayOffset), -1);
    // Show at least ~30 days of runway (5 weeks) so a short flow still has a
    // month to place steps into — matching the patient calendar's 30-day window.
    // Cover every step plus one runway week; the cap only guards against a typo'd
    // dayOffset (e.g. Dag 9999) freezing the grid — realistic flows (up to ~14
    // months) render in full so no step is ever hidden in the default view.
    const MIN_WEEKS = 5;
    const MAX_WEEKS = 60;
    const weekCount = Math.max(
      MIN_WEEKS,
      Math.min(Math.ceil((maxDay + 2) / 7), MAX_WEEKS),
    );
    const weeks: CalendarCell[][] = [];
    for (let w = 0; w < weekCount; w++) {
      const week: CalendarCell[] = [];
      for (let c = 0; c < 7; c++) {
        const day = w * 7 + c;
        week.push({
          key: `d${day}`,
          label: `Dag ${day}`,
          highlight: day === 0,
          chips: byDay.get(day) ?? [],
          onAdd: () => addItemOnDay(day),
        });
      }
      weeks.push(week);
    }
    return { weeks, truncated: maxDay >= MAX_WEEKS * 7 };
  }, [draft, view, editingIndex]);

  function patch(p: Partial<EditableFlow>) {
    setDraft((d) => (d ? { ...d, ...p } : d));
  }
  function patchItem(index: number, next: FlowItem) {
    setDraft((d) => {
      if (!d) return d;
      const items = d.items.slice();
      items[index] = next;
      return { ...d, items };
    });
  }
  function addItem() {
    setDraft((d) => {
      if (!d) return d;
      const maxDay = d.items.reduce((m, it) => Math.max(m, it.dayOffset), -1);
      return { ...d, items: [...d.items, newItem(maxDay + 1)] };
    });
  }
  // Clicking a day cell in the calendar appends a step on that relative day and
  // opens its form in the edit modal. The new step is appended, so its index is
  // the current item count.
  function addItemOnDay(day: number) {
    const newIndex = draft?.items.length ?? 0;
    setDraft((d) => (d ? { ...d, items: [...d.items, newItem(day)] } : d));
    setEditingIndex(newIndex);
  }
  function removeItem(index: number) {
    setDraft((d) =>
      d ? { ...d, items: d.items.filter((_, i) => i !== index) } : d,
    );
  }

  function payload() {
    if (!draft) throw new Error("no draft");
    return buildFlowPayload(draft);
  }

  async function onSave() {
    if (!draft) return;
    // The key is generated server-side from the name, so a name is required.
    if (isNew && !draft.name.trim()) {
      setError("Ge flödet ett namn först.");
      return;
    }
    setBusy("save");
    setError(null);
    setNotice(null);
    try {
      if (isNew) {
        const created = await createFlow(payload());
        setNotice("Flöde skapat.");
        navigate(`/flows/${encodeURIComponent(created.key)}`);
        return;
      }
      await saveFlowDraft(routeKey!, payload());
      setHadDraft(true);
      setBaseline(canonicalJson(payload()));
      setNotice("Utkast sparat.");
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  async function onPublish() {
    if (isNew || !routeKey) return;
    // Nothing to publish: no unsaved edits and no pending saved draft. Guard so a
    // stray Publicera (or double-click) can't mint a duplicate identical version.
    if (!dirty && !hadDraft) {
      setError(null);
      setConfirmPublish(false);
      setNotice("Inga ändringar att publicera.");
      return;
    }
    setBusy("publish");
    setError(null);
    setNotice(null);
    let draftSaved = false;
    try {
      // Persist the current editor state first (only if changed), so Publicera
      // publishes what's on screen — no separate "Spara utkast" step required.
      if (dirty) {
        await saveFlowDraft(routeKey, payload());
        setBaseline(canonicalJson(payload()));
        draftSaved = true;
      }
      const pub = await publishFlow(routeKey);
      setPublishedVersion(pub.version);
      setPublishedContent(pub);
      setShowDiff(false);
      setHadDraft(false);
      setConfirmPublish(false);
      setNotice(`Publicerad — version ${pub.version} är nu aktiv.`);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      // If we saved a draft but the publish itself failed, a draft now exists.
      if (draftSaved) setHadDraft(true);
      setError(e instanceof Error ? e.message : String(e));
      setConfirmPublish(false);
    } finally {
      setBusy(null);
    }
  }

  async function onArchive() {
    if (isNew || !routeKey) return;
    setBusy("archive");
    setError(null);
    try {
      await archiveFlow(routeKey);
      navigate("/flows");
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  }

  if (!draft) {
    return (
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl tracking-tight">Flöde</h1>
        {error ? (
          <span className="text-danger text-sm">{error}</span>
        ) : (
          <span className="text-textSecondary text-sm">Hämtar…</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-lg">
      <header className="flex items-start justify-between gap-md">
        <div>
          <h1 className="font-display text-3xl tracking-tight">
            {isNew ? "Nytt flöde" : draft.name || "Flöde"}
          </h1>
          {!isNew ? (
            <p className="text-xs text-textSecondary mt-xs">
              Publicerad version:{" "}
              <span className="font-medium text-textPrimary">
                {publishedVersion ? `v${publishedVersion}` : "ingen"}
              </span>{" "}
              •{" "}
              {dirty ? (
                <span className="text-warning font-medium">
                  Osparade ändringar
                </span>
              ) : hadDraft ? (
                <span className="text-warning font-medium">
                  Opublicerat utkast finns
                </span>
              ) : (
                <span>Inget opublicerat utkast</span>
              )}{" "}
              {/* TES-363 — what would publishing change? Only meaningful when
                  a published version exists to diff against. */}
              {publishedContent && (dirty || hadDraft) ? (
                <>
                  •{" "}
                  <button
                    type="button"
                    onClick={() => setShowDiff((s) => !s)}
                    className="text-brand hover:underline"
                  >
                    {showDiff ? "Dölj ändringar" : "Visa ändringar"}
                  </button>{" "}
                </>
              ) : null}
              • {draft.items.length} steg • Nyckel:{" "}
              <span className="font-mono">{routeKey}</span>
            </p>
          ) : null}
        </div>
        <div className="flex gap-sm shrink-0">
          {!isNew ? (
            <button
              type="button"
              onClick={onArchive}
              disabled={busy !== null}
              className="px-md py-sm rounded-md border border-surfaceMuted text-sm text-textSecondary hover:text-danger hover:border-danger disabled:opacity-60"
            >
              Arkivera
            </button>
          ) : null}
          <button
            type="button"
            onClick={onSave}
            disabled={busy !== null}
            className="px-md py-sm rounded-md border border-surfaceMuted text-sm hover:border-brand hover:text-brand disabled:opacity-60"
          >
            {busy === "save"
              ? "Sparar…"
              : isNew
                ? "Skapa flöde"
                : "Spara utkast"}
          </button>
          {!isNew ? (
            <button
              type="button"
              onClick={() => setConfirmPublish(true)}
              disabled={busy !== null || (!dirty && !hadDraft)}
              title={
                !dirty && !hadDraft ? "Inga ändringar att publicera" : undefined
              }
              className="px-md py-sm rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-60"
            >
              Publicera
            </button>
          ) : null}
        </div>
      </header>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm whitespace-pre-wrap">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="bg-success/10 border border-success/30 text-success rounded-md p-md text-sm">
          {notice}
        </div>
      ) : null}

      {/* TES-363 — draft-vs-published field diff ("Visa ändringar"). */}
      {diff && publishedVersion ? (
        <FlowDiffPanel diff={diff} publishedVersion={publishedVersion} />
      ) : null}

      {/* TES-363 — the TES-284 package header, previously round-tripped
          invisibly. Read-only banner (packages are seeded/imported; this
          editor doesn't author them) so the reviewer sees the gate. */}
      {draft.pkg.kind === "package" ? (
        <section className="bg-brand/5 border border-brand/30 rounded-md p-md flex flex-col gap-xs">
          <div className="flex items-center gap-sm">
            <span className="inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border bg-brand/10 text-brand border-brand/30">
              Innehållspaket
            </span>
            {draft.pkg.theme ? (
              <span className="text-sm font-medium">{draft.pkg.theme}</span>
            ) : null}
          </div>
          <p className="text-xs text-textSecondary">
            Risknivå:{" "}
            <span className="text-textPrimary">
              {draft.pkg.riskLevel ?? "—"}
            </span>{" "}
            • Kräver paketgodkännande:{" "}
            <span className="text-textPrimary">
              {draft.pkg.packageApprovalRequired ? "ja" : "nej"}
            </span>
            {draft.pkg.packageApprovalRequired ? (
              <>
                {" "}
                • Godkänns av:{" "}
                <span className="text-textPrimary">
                  {draft.pkg.requiredRole === "doctor"
                    ? "läkare"
                    : (draft.pkg.requiredRole ?? "—")}
                </span>
              </>
            ) : null}{" "}
            • Patientsynlighet:{" "}
            <span className="text-textPrimary">
              {draft.pkg.patientVisibility === "after_approval"
                ? "efter godkännande"
                : draft.pkg.patientVisibility === "immediate"
                  ? "direkt"
                  : "—"}
            </span>{" "}
            • Veckotema:{" "}
            <span className="text-textPrimary">
              {THEME_BEHAVIOR_LABELS[
                draft.pkg.themeBehavior ?? "preserve_weekly"
              ] ?? draft.pkg.themeBehavior}
              {draft.pkg.packageTheme ? " (egen temakopia)" : ""}
            </span>{" "}
            • Utgår:{" "}
            <span className="text-textPrimary">
              {draft.pkg.expiresAfterDays
                ? `${draft.pkg.expiresAfterDays} dagar efter godkännande`
                : "aldrig"}
            </span>
          </p>
          <p className="text-[10px] text-textSecondary">
            Paketfälten redigeras inte här — de följer med oförändrade när du
            sparar/publicerar (TES-284).
          </p>
        </section>
      ) : null}

      {/* TES-370 — read-only author metadata (the opaque "Ögonblick" fields
          from the content-authoring side). Only shown when present; never
          interpreted or editable here — it round-trips unchanged on
          save/publish. */}
      {draft.authorMetadata ? (
        <details className="bg-surface border border-surfaceMuted rounded-md p-md">
          <summary className="cursor-pointer text-sm font-medium">
            Författarmetadata
          </summary>
          <pre className="mt-sm text-xs text-textSecondary whitespace-pre-wrap break-all overflow-x-auto">
            {JSON.stringify(draft.authorMetadata, null, 2)}
          </pre>
          <p className="mt-xs text-[10px] text-textSecondary">
            Metadata från innehållsförfattaren — tolkas inte av plattformen och
            följer med oförändrad när du sparar/publicerar (TES-370).
          </p>
        </details>
      ) : null}

      {/* Flow-level settings */}
      <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
        <h2 className="font-display text-lg">Flödesinställningar</h2>
        <PlainField
          label="Namn"
          value={draft.name}
          onChange={(name) => patch({ name })}
        />
        <PlainField
          label="Beskrivning (valfri)"
          value={draft.description}
          onChange={(description) => patch({ description })}
        />
        <div className="grid grid-cols-2 gap-md">
          <label className="flex flex-col gap-xs">
            <span className="text-xs uppercase tracking-wide text-textSecondary">
              Trigger
            </span>
            <select
              value={draft.trigger.type}
              onChange={(e) =>
                patch({
                  trigger:
                    e.target.value === "event"
                      ? {
                          type: "event",
                          event: draft.trigger.event ?? TRIGGER_EVENTS[0].key,
                        }
                      : { type: "manual" },
                })
              }
              className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
            >
              <option value="manual">Manuell (coach tilldelar)</option>
              <option value="event">Händelse</option>
            </select>
          </label>
          {draft.trigger.type === "event" ? (
            <label className="flex flex-col gap-xs">
              <span className="text-xs uppercase tracking-wide text-textSecondary">
                Händelse
              </span>
              <select
                value={draft.trigger.event ?? TRIGGER_EVENTS[0].key}
                onChange={(e) =>
                  patch({
                    trigger: {
                      type: "event",
                      event: e.target.value as FlowEvent,
                    },
                  })
                }
                className="border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
              >
                {TRIGGER_EVENTS.map((ev) => (
                  <option key={ev.key} value={ev.key}>
                    {ev.label}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </section>

      {/* Steps — list or calendar */}
      <div className="flex items-center justify-between gap-md">
        <h2 className="font-display text-lg">Steg ({draft.items.length})</h2>
        <ViewToggle
          value={view}
          onChange={(v) => {
            setView(v);
            setEditingIndex(null); // the edit modal is calendar-scoped
          }}
        />
      </div>

      {view === "calendar" ? (
        <section className="flex flex-col gap-sm">
          <FlowCalendarGrid
            weeks={calendarWeeks}
            onChipClick={(c) => setEditingIndex(Number(c.id))}
          />
          <CalendarLegend
            items={[...new Set(draft.items.map((i) => i.moduleType))].map(
              (t) => ({
                key: t,
                color: moduleColor(t),
                label: MODULE_TYPE_LABELS[t],
              }),
            )}
          />
          <p className="text-[10px] text-textSecondary">
            Klicka på en dag för att lägga till ett steg, eller på ett steg för
            att redigera det.
            {calendarTruncated
              ? " Visar de första 60 veckorna — senare steg syns i listvyn."
              : ""}
          </p>
        </section>
      ) : (
        <>
          {/* Day-grouped items */}
          {groups.map(([day, entries]) => (
            <section key={day} className="flex flex-col gap-sm">
              <h3 className="font-display text-base text-textSecondary">
                {day === 0 ? "Dag 0 (start)" : `Dag ${day}`}
              </h3>
              {entries.map(({ item, index }) => (
                <ItemEditor
                  key={index}
                  item={item}
                  onChange={(next) => patchItem(index, next)}
                  onRemove={() => removeItem(index)}
                  articles={articles}
                />
              ))}
            </section>
          ))}

          <button
            type="button"
            onClick={addItem}
            className="self-start px-md py-sm rounded-md border border-dashed border-surfaceMuted text-sm text-textSecondary hover:text-brand hover:border-brand"
          >
            + Lägg till steg
          </button>
        </>
      )}

      {/* Calendar step editor modal — opened by adding or clicking a day chip. */}
      {editingIndex !== null && draft.items[editingIndex] ? (
        <div
          className="fixed inset-0 bg-black/30 flex items-start justify-center p-md z-50 overflow-y-auto"
          onClick={() => setEditingIndex(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="step-editor-title"
            className="bg-surface rounded-md p-lg max-w-2xl w-full flex flex-col gap-md my-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 id="step-editor-title" className="font-display text-xl">
                Steg • Dag {draft.items[editingIndex].dayOffset}
              </h3>
              <button
                type="button"
                onClick={() => setEditingIndex(null)}
                className="text-textSecondary hover:text-textPrimary text-sm"
              >
                Stäng
              </button>
            </div>
            <ItemEditor
              item={draft.items[editingIndex]}
              onChange={(next) => patchItem(editingIndex, next)}
              onRemove={() => {
                removeItem(editingIndex);
                setEditingIndex(null);
              }}
              articles={articles}
            />
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setEditingIndex(null)}
                className="px-md py-sm rounded-md bg-brand text-white text-sm hover:opacity-90"
              >
                Klar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmPublish ? (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-md z-50">
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="publish-flow-title"
            className="bg-surface rounded-md p-lg max-w-md w-full flex flex-col gap-md"
          >
            <h3 id="publish-flow-title" className="font-display text-xl">
              Publicera flöde
            </h3>
            <p className="text-textSecondary text-sm">
              Ändringarna publiceras som en ny, oföränderlig version. Nya
              tilldelningar använder den. Pågående resor påverkas inte.
            </p>
            <div className="flex gap-sm justify-end">
              <button
                type="button"
                onClick={() => setConfirmPublish(false)}
                disabled={busy !== null}
                className="px-md py-sm rounded-md text-textSecondary hover:bg-surfaceMuted text-sm"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={onPublish}
                disabled={busy !== null}
                className="px-md py-sm rounded-md bg-brand text-white text-sm hover:opacity-90 disabled:opacity-60"
              >
                {busy === "publish" ? "Publicerar…" : "Publicera"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
